import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  Send,
  Loader2,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const [postRes, commentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/social-posts/${postId}`),
        axios.get(`${API_URL}/api/social-posts/${postId}/comments`)
      ]);
      
      setPost(postRes.data);
      setComments(commentsRes.data.comments || []);
      
      // Check if liked
      if (user) {
        try {
          const likeRes = await axios.get(`${API_URL}/api/social-posts/${postId}/likes`, {
            withCredentials: true
          });
          setLiked(likeRes.data.liked);
        } catch (e) {
          setLiked(false);
        }
      }
    } catch (error) {
      toast.error('Failed to load post');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like posts');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/social-posts/${postId}/like`, {}, {
        withCredentials: true
      });
      
      setLiked(response.data.liked);
      setPost(prev => ({ ...prev, likes_count: response.data.likes_count }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to like post');
    }
  };

  const handleComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    
    if (!newComment.trim()) return;
    
    setPosting(true);
    try {
      const response = await axios.post(`${API_URL}/api/social-posts/${postId}/comments`, {
        text: newComment.trim()
      }, { withCredentials: true });
      
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      setPost(prev => ({ ...prev, comments_count: (prev.comments_count || 0) + 1 }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/social-posts/${postId}/comments/${commentId}`, {
        withCredentials: true
      });
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      setPost(prev => ({ ...prev, comments_count: Math.max(0, (prev.comments_count || 1) - 1) }));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const VerifiedName = ({ name, isVerified, className = '' }) => (
    <span className={`${isVerified ? 'font-bold text-blue-800' : ''} ${className}`}>
      {name}
    </span>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Guest Banner */}
      {!user && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-2xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm">Login to like, comment, and share</span>
              <Button
                onClick={() => navigate('/login')}
                size="sm"
                className="bg-white text-blue-600 hover:bg-blue-50 text-xs px-3 py-1 h-7"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Post */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Post Header */}
          <div className="p-4 flex items-center gap-3">
            <img
              src={getImageUrl(post.user_picture) || '/default-avatar.png'}
              alt={post.user_name}
              onClick={() => navigate(`/profile/${post.user_id}`)}
              className="w-12 h-12 rounded-full object-cover cursor-pointer"
            />
            <div>
              <VerifiedName 
                name={post.user_name} 
                isVerified={post.is_verified}
                className="cursor-pointer hover:underline"
                onClick={() => navigate(`/profile/${post.user_id}`)}
              />
              <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
            </div>
          </div>
          
          {/* Post Content */}
          <div className="px-4 pb-3">
            <p className="text-gray-800 whitespace-pre-wrap">{post.caption}</p>
          </div>
          
          {/* Post Media - Handle all possible image fields */}
          {(post.media_url || post.image_url || post.image) && (
            <div className="flex justify-center bg-gray-100">
              <img
                src={getImageUrl(post.media_url || post.image_url || post.image)}
                alt="Post media"
                style={{
                  maxHeight: "500px",
                  maxWidth: "100%",
                  objectFit: "contain"
                }}
              />
            </div>
          )}
          
          {/* Post Stats */}
          <div className="px-4 py-3 border-t border-b flex items-center gap-4 text-sm text-gray-500">
            <span>{post.likes_count || 0} likes</span>
            <span>{post.comments_count || 0} comments</span>
          </div>
          
          {/* Post Actions */}
          <div className="px-4 py-2 flex items-center gap-4 border-b">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                liked
                  ? 'text-red-500 bg-red-50'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
              <span>Like</span>
            </button>
            
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600">
              <MessageCircle className="h-5 w-5" />
              <span>Comment</span>
            </button>
            
            <ShareButton
              title={post.caption?.substring(0, 100) || 'Check out this post'}
              description={post.user_name ? `Post by ${post.user_name}` : ''}
              url={window.location.href}
              image={getImageUrl(post.media_url || post.image_url || post.image)}
              shareType="post"
              itemId={postId}
              variant="default"
              className="ml-auto"
            />
          </div>

          {/* Comments Section */}
          <div className="p-4">
            {/* Comment Input */}
            {user && (
              <div className="flex gap-3 mb-4">
                <img
                  src={getImageUrl(user.picture) || '/default-avatar.png'}
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1 flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    maxLength={300}
                    onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                  />
                  <Button
                    onClick={handleComment}
                    disabled={posting || !newComment.trim()}
                    size="icon"
                  >
                    {posting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="flex gap-3">
                  <img
                    src={getImageUrl(comment.user_picture) || '/default-avatar.png'}
                    alt={comment.user_name}
                    onClick={() => navigate(`/profile/${comment.user_id}`)}
                    className="w-8 h-8 rounded-full object-cover cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="bg-gray-100 rounded-lg px-3 py-2">
                      <VerifiedName 
                        name={comment.user_name} 
                        isVerified={comment.is_verified}
                        className="text-sm"
                      />
                      <p className="text-gray-800 text-sm">{comment.text}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{formatDate(comment.created_at)}</span>
                      {(comment.user_id === user?.user_id || user?.is_admin) && (
                        <button
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {comments.length === 0 && (
                <p className="text-center text-gray-500 py-4">No comments yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
